import React, { useRef, useState } from 'react'
import styled from 'styled-components'

import useDispatch from 'hooks/useDispatch'
import { updateFilters } from '../../../../actions/searchActions'
import CloseIcon from '../../../../components/icons/AirbnbClose'
import InvisibleButton from '../../../../components/display/InvisibleButton'

import FilterPanelActions from '../FilterPanelActions'
import { FilterPanel, CloseButton, PanelHeader } from '../shared'

import AvailableWeekends from './AvailableWeekends'
import SpecificDates from './SpecificDates'

type DateSearchType = 'specific-dates' | 'available-weekends'

const ViewTabs = styled.div`
  margin: 0 -16px;
  padding: 0 16px;

  border-bottom: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};

  margin-bottom: 24px;

  & > *:not(:last-child) {
    margin-right: 24px;
  }
`

const Tab = styled(InvisibleButton)<{ active: boolean }>`
  position: relative;
  padding: 12px 0;
  font-weight: 400;
  color: ${({ theme, active }) =>
    active
      ? theme?.['-ds-core-400']?.value
      : theme?.['-ds-neutral-700']?.value};

  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};

  ${({ active, theme }) =>
    active &&
    `
    border-bottom: 2px solid ${theme?.['-ds-core-400']?.value};
  `}
`

const getInitialDateSearchType = (
  filters: GQL_SearchFilters
): DateSearchType => {
  if (filters.weekendMonths?.length > 0) {
    return 'available-weekends'
  }

  return 'specific-dates'
}

type Props = {
  open: boolean
  filters: GQL_SearchFilters
  onClose: () => any
  resultsCount?: number
}

const DatesFilter = ({ open, filters, onClose, resultsCount }: Props) => {
  const initialDateSearchType = getInitialDateSearchType(filters)
  const [dateSearchType, setDateSearchType] = useState<DateSearchType>(
    initialDateSearchType
  )

  const dispatch = useDispatch()

  const updateDatesFilters = (
    nextDatesFilters: Pick<
      GQL_SearchFilters,
      'startDate' | 'endDate' | 'weekendMonths'
    >
  ) => {
    dispatch(updateFilters({ ...filters, ...nextDatesFilters }))
  }

  const scrollContainer = useRef<HTMLDivElement>()

  const resetScroll = () => {
    scrollContainer.current.scrollTo(0, 0)
  }

  const handleClosePanel = () => {
    resetScroll()
    onClose()
  }

  const handleApply = () => {
    handleClosePanel()
  }

  const handleResetFilters = () => {
    updateDatesFilters({ startDate: null, endDate: null, weekendMonths: null })
    onClose()
  }

  return (
    <FilterPanel className={`${open ? 'show' : ''}`} ref={scrollContainer}>
      <PanelHeader>
        <CloseButton aria-label="Close dates filter" onClick={handleClosePanel}>
          <CloseIcon />
        </CloseButton>
        <span>Dates</span>
      </PanelHeader>

      <ViewTabs>
        <Tab
          active={dateSearchType === 'specific-dates'}
          onClick={() => setDateSearchType('specific-dates')}
        >
          Specific dates
        </Tab>
        <Tab
          active={dateSearchType === 'available-weekends'}
          onClick={() => setDateSearchType('available-weekends')}
        >
          Available weekends
        </Tab>
      </ViewTabs>

      {dateSearchType === 'specific-dates' && (
        <SpecificDates filters={filters} open={open} />
      )}

      {dateSearchType === 'available-weekends' && (
        <AvailableWeekends filters={filters} />
      )}

      <FilterPanelActions
        onClear={handleResetFilters}
        onView={handleApply}
        resultsCount={resultsCount}
      />
    </FilterPanel>
  )
}

export default DatesFilter
